<template>
  <OurPage :back-route="backRoute" class="access-groups" :title="pageTitle" width="md">
    <DeleteArticleModal
      :show.sync="isShownDeleteModal"
      :article-id="articleId"
      :article-name="articleName"
      @deleteArticle="getData"
    />

    <DeleteSectionModal
      :show.sync="isShownDeleteSectionModal"
      :knowledge-base-sections-id="subsectionId"
      :knowledge-base-sections-name="subsectionName"
      @deleteKnowledgeBaseSection="getData"
    />

    <template #header-right>
      <OurLink v-if="!isUserDealer && getPermission['knowledge_base.create']" :route="addRoute">
        <OurButton :text="$t('button.add')" />
      </OurLink>
    </template>

    <OurTableList
      :have-permission-delete="getPermission['knowledge_base.delete']"
      :have-permission-edit="getPermission['knowledge_base.update']"
      :list="items"
      @clickEdit="onClickEdit"
      @clickDelete="onClickDelete"
      @clickChapter="onClickItem"
      @dragSort="dragHandler"
    >
      <template #default="{ item }">
        <div class="list-item-left-title cursor-pointer" @click="onClickItem(item.id)">
          <span v-if="isSection(item)" class="chapter-subsection-label">
            {{ $t("label.selectSection") }}
          </span>
          <span>{{ item.name }}</span>
        </div>
      </template>
    </OurTableList>
  </OurPage>
</template>

<script>
import { mapGetters } from "vuex";
import apolloMixin from "@/mixins/apollo.mixin";
import DeleteArticleModal from "@/views/settings/base-knowledge/chapter/delete/Modal.vue";
import DeleteSectionModal from "@/views/settings/base-knowledge/delete/Modal.vue";
import { reorderKnowledgeBaseItemsMutation } from "@/views/settings/base-knowledge/index/gql/mutations";
import { getKnowledgeBaseSectionMeta } from "@/views/settings/base-knowledge/services/sections";
import {
  fetchKnowledgeBaseItems,
  isKnowledgeBaseSection,
  toKnowledgeBaseItemRefs,
} from "@/views/settings/base-knowledge/services/items";

export default {
  name: "Chapter",
  components: { DeleteArticleModal, DeleteSectionModal },
  mixins: [apolloMixin],
  data() {
    return {
      items: [],
      isShownDeleteModal: false,
      isShownDeleteSectionModal: false,
      articleId: "",
      articleName: "",
      subsectionId: "",
      subsectionName: "",
      currentParentId: null,
      currentParentName: "",
      currentSectionName: "",
    };
  },
  computed: {
    ...mapGetters("user", ["isUserDealer", "getPermission"]),

    addRoute: () => ({ name: "ChapterAdd" }),

    pageTitle() {
      const title = this.$t("title.knowledgeBase.chapter");

      return this.currentSectionName ? `${title}: ${this.currentSectionName}` : title;
    },

    backRoute() {
      if (this.currentParentId) {
        return {
          name: "Chapter",
          params: { id: this.currentParentId },
          title: this.currentParentName || this.$t("title.knowledgeBase.chapter"),
        };
      }

      return {
        name: "BaseKnowledge",
        title: this.$t("title.knowledgeBase.chapters"),
      };
    },
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler: "getData",
    },
  },
  methods: {
    isSection: isKnowledgeBaseSection,

    findItem(id) {
      return this.items.find((item) => String(item.id) === String(id));
    },

    async getData() {
      const sectionId = this.$route.params.id;

      this.loader("on");

      try {
        const [items, sectionMeta] = await Promise.all([
          fetchKnowledgeBaseItems(sectionId),
          getKnowledgeBaseSectionMeta(sectionId),
        ]);

        if (this.$route.params.id !== sectionId) {
          return;
        }

        this.items = items;
        this.currentParentId = sectionMeta.parentId;
        this.currentParentName = sectionMeta.parentName || "";
        this.currentSectionName = sectionMeta.name || "";
      } finally {
        if (this.$route.params.id === sectionId) {
          this.loader("off");
        }
      }
    },

    onClickItem(id) {
      const item = this.findItem(id);

      if (!item) {
        return;
      }

      if (this.isSection(item)) {
        this.$router.push({ name: "Chapter", params: { id: item.id } });

        return;
      }

      this.onClickEdit(item.id);
    },

    onClickEdit(id) {
      const item = this.findItem(id);

      if (!item) {
        return;
      }

      if (this.isSection(item)) {
        this.$router.push({ name: "BaseKnowledgeEdit", params: { id: item.id } });

        return;
      }

      this.$router.push({ name: "ChapterEdit", params: { articleId: id } });
    },

    onClickDelete(id) {
      const item = this.findItem(id);

      if (!item) {
        return;
      }

      if (this.isSection(item)) {
        this.isShownDeleteSectionModal = true;
        this.subsectionId = item.id;
        this.subsectionName = item.name;

        return;
      }

      this.isShownDeleteModal = true;
      this.articleId = item.id;
      this.articleName = item.name;
    },

    async dragHandler() {
      const order = toKnowledgeBaseItemRefs(this.items);

      await this.$post(
        reorderKnowledgeBaseItemsMutation,
        { order, parentId: this.$route.params.id },
        {
          delaySuccessNotify: false,
        }
      );
    },
  },
};
</script>

<style lang="postcss" scoped>
.chapter-subsection-label {
  @apply mb-0.5 block font-bold;
}

.list-item-left-title {
  @apply flex flex-col text-base font-normal text-gray-900;
  word-break: break-word;
}
</style>
